/**
 * Redux slice for org-scoped test location details (Site NPI modal).
 *
 * @module testLocationDetailsSlice
 */
import { createSlice, PayloadAction } from "@reduxjs/toolkit";
import { getCurrentFacilityId } from "@risalabs_frontend_org/auth-wrapper";
import { SiteNpiDetailsService } from "../../api/services/siteNpiDetails/siteNpiDetailsService";
import { TestLocationDetail } from "../../api/services/siteNpiDetails/siteNpiDetailsTypes";
import { AppDispatch, RootState } from "../store/store";

/** Cached test location details for the active organization. */
export interface TestLocationDetailsState {
  /** Facility id that `locations` was loaded for; null before first fetch. */
  orgId: string | null;
  /** Test location rows returned by the backend. */
  locations: TestLocationDetail[];
  loading: boolean;
  error: string | null;
}

const initialState: TestLocationDetailsState = {
  orgId: null,
  locations: [],
  loading: false,
  error: null,
};

export const testLocationDetailsSlice = createSlice({
  name: "testLocationDetails",
  initialState,
  reducers: {
    setTestLocationDetailsLoading: (state, action: PayloadAction<boolean>) => {
      state.loading = action.payload;
      if (action.payload) {
        state.error = null;
      }
    },
    setTestLocationDetails: (
      state,
      action: PayloadAction<{ orgId: string; locations: TestLocationDetail[] }>,
    ) => {
      state.orgId = action.payload.orgId;
      state.locations = action.payload.locations;
      state.loading = false;
      state.error = null;
    },
    setTestLocationDetailsError: (state, action: PayloadAction<string>) => {
      state.error = action.payload;
      state.loading = false;
    },
    resetTestLocationDetails: (state) => {
      state.orgId = null;
      state.locations = [];
      state.loading = false;
      state.error = null;
    },
  },
});

export const {
  setTestLocationDetailsLoading,
  setTestLocationDetails,
  setTestLocationDetailsError,
  resetTestLocationDetails,
} = testLocationDetailsSlice.actions;

/**
 * Loads all test location details for the current organization.
 *
 * Skips when the same org is already cached unless `force` is true.
 * Non-forced calls also skip while a fetch is already in flight. Forced
 * refreshes (e.g. post-save) proceed even if loading so dropdowns are not
 * left stale. Org scope is resolved from the active facility id and the
 * auth token on the backend request.
 *
 * @param force - When true, refetches even if data for this org is already present.
 */
export const fetchAllTestLocationForOrg =
  (force = false) =>
  async (dispatch: AppDispatch, getState: () => RootState) => {
    const orgId = getCurrentFacilityId() || "";
    const { testLocationDetails } = getState();

    if (!orgId) {
      return;
    }

    if (!force && testLocationDetails.orgId === orgId) {
      return;
    }

    if (!force && testLocationDetails.loading) {
      return;
    }

    dispatch(setTestLocationDetailsLoading(true));

    try {
      const locations = await SiteNpiDetailsService.getAllTestLocationForOrg();
      dispatch(setTestLocationDetails({ orgId, locations }));
    } catch (error) {
      const message =
        error instanceof Error
          ? error.message
          : "Failed to fetch test location details";
      dispatch(setTestLocationDetailsError(message));
    }
  };

export default testLocationDetailsSlice.reducer;
